import { ButtonGradientPrimary, Card, Colors, Icon, Text } from '@amelio/ui-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { readTargetStepTitle } from '../GrowthTab/targetPosition';

import {
    useGetEmployeeCompetencyAssignment,
    useGetEmployeeGrowth,
} from '@/api/performance-profile';
import { getLocalizedText } from '@/utils/localization';

interface CompetencyProfileCardProps {
    employeeId: string;
    /** Absent where the card is only read, as on an employee's own profile. */
    onEditCurrentPosition?: () => void;
    isEmployeeInactive?: boolean;
    /**
     * Whether the position the employee aims for belongs on this screen. The assessment tab reads
     * the profile as it stands today, so it turns this off; the growth tab is where the target
     * position is discussed, and it shows it three times over.
     */
    showsTargetPosition?: boolean;
    /**
     * `canAssignCompetencyProfiles`, the policy the server enforces on the assignment endpoint.
     * Handed down by the page rather than read here, so one screen has one answer to it.
     */
    canAssignCompetencyProfiles?: boolean;
}

export const CompetencyProfileCard = ({
    employeeId,
    onEditCurrentPosition,
    isEmployeeInactive,
    showsTargetPosition = true,
    canAssignCompetencyProfiles = false,
}: CompetencyProfileCardProps) => {
    const { t } = useTranslation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { data: assignment, isLoading, isError } = useGetEmployeeCompetencyAssignment(employeeId);
    // The growth read is refused on a manager's own profile: the desired position is then simply
    // not shown, the card has nothing to claim about it. The target position belongs to the growth
    // tab, so a screen that does not show it does not ask for it either.
    const { data: growth } = useGetEmployeeGrowth(showsTargetPosition ? employeeId : undefined);
    const desiredPositionTitle = showsTargetPosition ? readTargetStepTitle(growth) : null;

    const profileName = assignment?.profileName ? getLocalizedText(assignment.profileName) : null;
    const hasValidAssignment = assignment && profileName;
    // Three states, not two. A failed read tells us nothing about whether the employee has a
    // profile, so it must not render the same "none assigned" message as an empty result: this
    // endpoint is refused for anyone without a competency permission (#14980), and the card was
    // reporting an absence it had never observed. Covers any failure, not only a refusal.
    const isAssignmentUnavailable = isError;
    // One condition for both ways into the assignment flow: the reader may assign, the employee can
    // still be assigned to, and this copy of the card was given somewhere to go.
    const canAssignProfile =
        canAssignCompetencyProfiles && !isEmployeeInactive && Boolean(onEditCurrentPosition);
    // Offered only where the absence has actually been observed. A read that failed says nothing
    // about whether a profile exists, so it gets the sentence and no action.
    const offersAssignment = canAssignProfile && !hasValidAssignment && !isAssignmentUnavailable;
    const additionalCompetencies =
        assignment?.additionalCompetencyGroups?.reduce(
            (total, group) => total + group.competencies.length,
            0,
        ) ?? 0;

    const handleEditClick = () => {
        setDropdownOpen((prev) => !prev);
    };

    const handleCurrentPositionClick = () => {
        setDropdownOpen(false);
        onEditCurrentPosition?.();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!dropdownOpen) return;

            // Use composedPath to correctly resolve targets across Shadow DOM
            // boundaries, since event.target is retargeted to the shadow host.
            const path = event.composedPath();
            const isClickInsideButton = buttonRef.current
                ? path.includes(buttonRef.current)
                : false;
            const isClickInsideDropdown = dropdownRef.current
                ? path.includes(dropdownRef.current)
                : false;

            if (!isClickInsideButton && !isClickInsideDropdown) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    return (
        <Card
            borderRadius={'2xl'}
            // No top margin: the tab already spaces its sections, and a second spacing declared
            // here pushed this one block 16px further down than every other. One inset from 768px,
            // the same on all four sides, which is the height the reference draws.
            className={'relative px-6 py-6 md:!p-[34px]'}
            zIndex={20}
            data-testid={'competency-profile-card'}
        >
            <div className={'flex items-start justify-between'}>
                <div className={'ds-gap-2xl flex flex-col items-start sm:flex-row'}>
                    <div
                        className={
                            'bg-base-50 flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full'
                        }
                    >
                        <Icon
                            name={'design-colorfilter'}
                            size={36}
                            color={Colors.PRIMARY}
                        />
                    </div>

                    <div className={'ds-gap-2xl flex flex-col'}>
                        <h3 className={'text-font-xl text-navy font-medium'}>
                            {t('performanceProfile.competencyProfile.title')}
                        </h3>

                        {!isLoading && (
                            <div className={'flex flex-wrap items-center gap-6'}>
                                {hasValidAssignment ? (
                                    <div
                                        className={
                                            'flex flex-col items-center gap-[6px] sm:flex-row'
                                        }
                                    >
                                        <span className={'text-obj-gray'}>
                                            {t(
                                                'performanceProfile.competencyProfile.currentPosition',
                                            )}
                                            :
                                        </span>
                                        <span
                                            className={
                                                'ds-px-md ds-py-xs !bg-growth-violet flex flex-col items-center rounded-full text-sm leading-[18px] font-semibold text-white sm:flex-row'
                                            }
                                            data-testid={'current-position-badge'}
                                        >
                                            {profileName}
                                            {additionalCompetencies > 0 && (
                                                <span className={'ml-1 text-[9px] font-normal'}>
                                                    {`+ ${t(
                                                        'performanceProfile.competencyProfile.competencies',
                                                        { count: additionalCompetencies },
                                                    )}`}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        <Text
                                            color={Colors.BASE_400}
                                            className={'italic'}
                                            data-testid={
                                                isAssignmentUnavailable
                                                    ? 'competency-profile-unavailable-message'
                                                    : 'no-competency-profile-message'
                                            }
                                        >
                                            {isAssignmentUnavailable
                                                ? t(
                                                      'performanceProfile.competencyProfile.assignmentUnavailable',
                                                  )
                                                : t(
                                                      'performanceProfile.competencyProfile.noAssignment',
                                                  )}
                                        </Text>

                                        {offersAssignment && (
                                            // The one way in on this screen. The skillset card
                                            // below used to offer it too, but its empty state
                                            // follows the filtered assessment, not the assignment.
                                            <ButtonGradientPrimary
                                                size={'sm'}
                                                fitContent
                                                onClick={onEditCurrentPosition}
                                                leftIcon={
                                                    <Icon
                                                        name={'users-profile-add'}
                                                        color={Colors.WHITE}
                                                        size={'md'}
                                                    />
                                                }
                                                dataTestId={'assign-competency-profile-button'}
                                            >
                                                {t(
                                                    'performanceProfile.competencyProfile.assignProfile',
                                                )}
                                            </ButtonGradientPrimary>
                                        )}
                                    </>
                                )}
                                {desiredPositionTitle && (
                                    <div
                                        className={
                                            'flex flex-col items-center gap-[6px] sm:flex-row'
                                        }
                                    >
                                        <span className={'text-obj-gray'}>
                                            {t('performanceProfile.competencyProfile.targetStep')}:
                                        </span>
                                        <span
                                            className={
                                                'ds-px-md ds-py-xs text-navy text-body-sm rounded-full border border-(--color-primary) font-semibold'
                                            }
                                            data-testid={'desired-position-badge'}
                                        >
                                            {desiredPositionTitle}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {canAssignProfile && (
                    <div className={'relative'}>
                        <button
                            ref={buttonRef}
                            onClick={handleEditClick}
                            // Padding kept, not swapped for `develop`'s bare `hover:opacity-70`:
                            // without it the button shrinks to its icon and the click target
                            // falls under the 24 px the repo asks for.
                            className={
                                'hover:bg-growth-tint text-obj-stepper hover:text-obj-gray rounded-full p-2 transition-colors'
                            }
                            aria-label={'Edit'}
                            data-testid={'edit-button'}
                        >
                            <Icon
                                name={'edit-edit-2'}
                                size={18}
                                color={Colors.NAVY}
                            />
                        </button>

                        {dropdownOpen && (
                            <div
                                ref={dropdownRef}
                                className={
                                    'border-growth-tint pointer-events-auto absolute top-full right-0 mt-1 min-w-48 rounded-lg border bg-white shadow-lg'
                                }
                                style={{ zIndex: 9999 }}
                                data-testid={'edit-dropdown'}
                            >
                                <div
                                    className={
                                        'border-growth-line text-obj-gray border-b px-4 py-3 text-[12px] font-medium tracking-wide'
                                    }
                                >
                                    {t('performanceProfile.competencyProfile.editDropdownHeader')}
                                </div>
                                <button
                                    onClick={handleCurrentPositionClick}
                                    className={
                                        'text-navy hover:bg-growth-tint w-full px-4 py-3 text-left text-[14px] font-medium'
                                    }
                                    data-testid={'dropdown-current-position'}
                                >
                                    {t('performanceProfile.competencyProfile.editCurrentPosition')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};
